// Scrapbook recap — the canon default share artifact (ADR-0017 amended).
//
// Port of design-canon `screens-recap.jsx → ScrapbookRecap` at 1080×1920 story
// scale. Composes: cream paper bg + grain, smart-inferred display headline +
// Caveat subtitle, 3 stat tiles (drinks / stops / elapsed), up to 6 tilted
// polaroid photos with handwritten venue captions, scrapbook tape strips, an
// orange "legendary" stamp accent, an optional leaderboard chip, and the
// stmbl.party + date footer.
//
// Why client-side Canvas (not Satori / server)? Same call as ADR-0017 Tier 1:
// the user taps Share → result lands in <2s, zero server roundtrip, the
// browser's font + image cache makes re-renders instant. The scrapbook is a
// richer composition than the wrapped poster but uses the same primitives.

import { canvasToBlob, makeCanvas } from '../canvasExport'
import { RECAP_PALETTE, stampSvg, svgToImage, wordmarkSvg } from '../recapAssets'
import { seededRandom } from '../seededRandom'
import { inferSmartTitle } from './smartTitle'

export type ScrapbookVenue = {
  name: string
  arrivedAt: Date
  leftAt: Date | null
  /** Optional URL to a selfie taken at this venue — drawn into the polaroid. */
  photoUrl?: string | null
}

export type ScrapbookLeaderboardEntry = {
  /** Display name; "you" is rendered as-is. */
  name: string
  /** 1-indexed rank position from the night's friend cohort. */
  rank: number
  drinkCount: number
  venueName?: string | null
  /** True if this row is the current user — gets a highlight chip. */
  isSelf?: boolean
}

/**
 * F3: explicit per-selfie card source. When provided, the scrapbook draws one
 * polaroid per entry (rather than collapsing to one polaroid per venue via the
 * legacy `venues[].photoUrl` path). Most recent first, capped at 6 by the
 * caller — the layout has 6 slots.
 */
export type ScrapbookSelfie = {
  url: string
  venueName: string
  capturedAt: string // ISO timestamp
}

export type ScrapbookMetadata = {
  nightId: string
  nightDate: Date
  /** Wall-clock start → end of the night; drives the "elapsed" stat. */
  startedAt: Date
  endedAt: Date | null
  venues: ScrapbookVenue[]
  drinkTotals: { beer: number; wine: number; cocktail: number; shot: number }
  participants?: string[]
  squadName?: string | null
  /**
   * Optional friend leaderboard — when ≥ 1 friend was out, the share PNG
   * shows a small chip at the bottom. Order is by drinkCount desc; the
   * caller is responsible for that sort.
   */
  leaderboard?: ScrapbookLeaderboardEntry[]
  /**
   * F3: per-selfie polaroid sources. When set + non-empty, takes precedence
   * over the per-venue photoUrl fallback so N selfies yield N polaroids.
   */
  selfies?: ScrapbookSelfie[]
}

export type ScrapbookOpts = {
  format?: 'image/png'
  /**
   * Override the inferred headline. Tests pass this for visual goldens; the
   * production code path leaves it undefined.
   */
  headlineOverride?: string
}

const POSTER_W = 1080
const POSTER_H = 1920

// Cream paper background — the scrapbook page colour, slightly warmer than
// the wrapped poster's pure white so it reads as "old photo album".
const PAPER = '#f5ead0'
const PAPER_EDGE = '#e8dab8'

/** Render the scrapbook recap as a 1080×1920 PNG blob. */
export async function generateScrapbookCollage(
  photos: Blob[],
  metadata: ScrapbookMetadata,
  opts: ScrapbookOpts = {},
): Promise<Blob> {
  const rand = seededRandom(`scrapbook:${metadata.nightId}`)
  const totalDrinks =
    metadata.drinkTotals.beer +
    metadata.drinkTotals.wine +
    metadata.drinkTotals.cocktail +
    metadata.drinkTotals.shot
  const venueCount = metadata.venues.length

  const smart = inferSmartTitle({
    nightDate: metadata.nightDate,
    venueCount,
    drinkTotals: metadata.drinkTotals,
    primaryVenueName: metadata.venues[0]?.name ?? null,
    participants: metadata.participants,
    squadName: metadata.squadName,
  })
  const headline = opts.headlineOverride ?? smart.headline

  // ── Asset preload ────────────────────────────────────────────────────────
  // Wordmark + wrapped-style stamp + photo blobs all decoded in parallel.
  // F3: prefer per-selfie cards when `metadata.selfies` is present + non-empty
  // (one polaroid per selfie). Fall back to legacy venue-based grouping for
  // older callers that still pass [].
  const polaroidCards: PolaroidCard[] = buildPolaroidCards(photos, metadata)
  const [wordmark, stamp, ...photoImgs] = await Promise.all([
    svgToImage(wordmarkSvg({ width: 520, fill: RECAP_PALETTE.INK })),
    svgToImage(stampSvg('wrapped', { rotate: -6 })),
    ...polaroidCards.map((card) => loadImage(card.source).catch(() => null)),
  ])

  const canvas = makeCanvas(POSTER_W, POSTER_H)
  const ctx = canvas.getContext('2d') as
    | CanvasRenderingContext2D
    | OffscreenCanvasRenderingContext2D
    | null
  if (!ctx) {
    throw new Error('generateScrapbookCollage: 2d context unavailable')
  }

  // ── Background — cream paper with subtle vertical fade ───────────────────
  const bg = ctx.createLinearGradient(0, 0, 0, POSTER_H)
  bg.addColorStop(0, PAPER)
  bg.addColorStop(1, PAPER_EDGE)
  ctx.fillStyle = bg
  ctx.fillRect(0, 0, POSTER_W, POSTER_H)

  drawPaperGrain(ctx, rand)
  drawScrapbookTape(ctx, 110, 60, -8)
  drawScrapbookTape(ctx, POSTER_W - 290, 80, 7)

  // ── Eyebrow + date stamp at top ──────────────────────────────────────────
  ctx.fillStyle = RECAP_PALETTE.INK
  ctx.font = "500 32px 'Special Elite','Courier New',monospace"
  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'
  ctx.fillText(formatDate(metadata.nightDate).toUpperCase(), 96, 240)

  ctx.textAlign = 'right'
  drawStampBadge(ctx, POSTER_W - 96, 240, `CLAIMED ${Math.max(venueCount, 1)}`)

  // ── Headline (Bagel Fat One) + Caveat subtitle ───────────────────────────
  ctx.fillStyle = RECAP_PALETTE.INK
  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'
  ctx.font = "400 168px 'Bagel Fat One','Atkinson Hyperlegible',system-ui,sans-serif"
  drawWrappedText(ctx, headline, 96, 380, POSTER_W - 192, 160, 2)

  ctx.fillStyle = '#4a1f5e' // --plum from canon
  ctx.font = "500 72px 'Caveat','Atkinson Hyperlegible',cursive"
  ctx.fillText(smart.subtitle, 96, 640)

  // ── 3 stat tiles — DRINKS / STOPS / ELAPSED ──────────────────────────────
  const elapsedLabel = formatElapsed(metadata.startedAt, metadata.endedAt)
  drawStatTiles(ctx, 96, 720, POSTER_W - 192, [
    {
      value: String(totalDrinks),
      label: 'drinks',
      bg: RECAP_PALETTE.PINK,
      fg: RECAP_PALETTE.PAPER,
    },
    {
      value: String(Math.max(venueCount, 1)),
      label: 'stops',
      bg: RECAP_PALETTE.LIME,
      fg: RECAP_PALETTE.INK,
    },
    {
      value: elapsedLabel,
      label: 'elapsed',
      bg: RECAP_PALETTE.TEAL,
      fg: RECAP_PALETTE.INK,
    },
  ])

  // ── Polaroid grid — up to 6 cards with handwritten captions ─────────────
  // F3: cards come from `metadata.selfies` when present (one per selfie), else
  // fall back to one-per-venue via `metadata.venues[].photoUrl`. Captions vary
  // by source.
  const polaroidTop = 1010
  const polaroidArea = { x: 80, y: polaroidTop, w: POSTER_W - 160, h: 580 }
  drawPolaroidGrid(ctx, polaroidArea, polaroidCards.slice(0, 6), photoImgs, rand)

  // ── Orange "legendary" stamp scribble ────────────────────────────────────
  ctx.save()
  ctx.translate(POSTER_W - 240, polaroidTop + polaroidArea.h - 30)
  ctx.rotate((-8 * Math.PI) / 180)
  ctx.fillStyle = RECAP_PALETTE.ORANGE
  ctx.font = "500 80px 'Caveat','Atkinson Hyperlegible',cursive"
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(`★ ${smart.stamp} ★`, 0, 0)
  ctx.restore()

  // ── Optional friend-leaderboard chip ─────────────────────────────────────
  let footerY = POSTER_H - 220
  if (metadata.leaderboard && metadata.leaderboard.length > 0) {
    drawLeaderboardChip(ctx, 96, 1640, POSTER_W - 192, metadata.leaderboard.slice(0, 3))
    footerY = POSTER_H - 140
  }

  // ── Footer: wordmark pill + stmbl.party + tilted "wrapped" stamp ─────────
  const wordmarkScale = 0.38
  const wordmarkW = wordmark.width * wordmarkScale
  const wordmarkH = wordmark.height * wordmarkScale
  ctx.drawImage(wordmark, 96, footerY - wordmarkH, wordmarkW, wordmarkH)

  ctx.fillStyle = RECAP_PALETTE.INK
  ctx.font = "500 28px 'Special Elite','Courier New',monospace"
  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'
  ctx.fillText('stmbl.party', 96, footerY + 24)

  const stampScale = 0.55
  const stampW = stamp.width * stampScale
  const stampH = stamp.height * stampScale
  ctx.drawImage(stamp, POSTER_W - stampW - 96, footerY - stampH + 8, stampW, stampH)

  const format = opts.format ?? 'image/png'
  return canvasToBlob(canvas, format as 'image/webp', 1)
}

// ── helpers ────────────────────────────────────────────────────────────────

type AnyCtx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D

function drawPaperGrain(ctx: AnyCtx, rand: () => number): void {
  // Tiny speck overlay — keeps the cream paper feeling "scanned", not flat.
  // 1800 dots is enough to feel textured at 1080×1920 without bloating the
  // PNG by much (PNG run-length packs near-uniform regions efficiently).
  ctx.save()
  ctx.globalAlpha = 0.08
  ctx.fillStyle = '#3b2c1a'
  for (let i = 0; i < 1800; i++) {
    const x = rand() * POSTER_W
    const y = rand() * POSTER_H
    const r = 0.6 + rand() * 1.6
    ctx.beginPath()
    ctx.arc(x, y, r, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.restore()
}

function drawScrapbookTape(ctx: AnyCtx, x: number, y: number, rotateDeg: number): void {
  // Translucent yellow washi tape strip with two parallel torn edges.
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate((rotateDeg * Math.PI) / 180)
  ctx.fillStyle = 'rgba(246, 233, 39, 0.6)' // canon --yellow @ 60%
  ctx.fillRect(0, 0, 180, 56)
  ctx.fillStyle = 'rgba(246, 233, 39, 0.35)'
  ctx.fillRect(0, 0, 180, 12)
  ctx.fillRect(0, 44, 180, 12)
  ctx.restore()
}

function drawStampBadge(ctx: AnyCtx, x: number, y: number, text: string): void {
  // Stamp badge — outlined pill, Special Elite type. Used for "CLAIMED N".
  const padX = 22
  ctx.font = "500 28px 'Special Elite','Courier New',monospace"
  const w = ctx.measureText(text).width + padX * 2
  const h = 56
  ctx.save()
  ctx.translate(x - w, y - h + 12)
  ctx.fillStyle = RECAP_PALETTE.PAPER
  ctx.strokeStyle = RECAP_PALETTE.ORANGE
  ctx.lineWidth = 4
  ctx.setLineDash([8, 4])
  roundRect(ctx, 0, 0, w, h, 8)
  ctx.fill()
  ctx.stroke()
  ctx.setLineDash([])
  ctx.fillStyle = RECAP_PALETTE.ORANGE
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, w / 2, h / 2 + 2)
  ctx.restore()
}

type StatTile = { value: string; label: string; bg: string; fg: string }

function drawStatTiles(ctx: AnyCtx, x: number, y: number, w: number, tiles: StatTile[]): void {
  const gap = 24
  const tileW = (w - gap * (tiles.length - 1)) / tiles.length
  const tileH = 200
  for (let i = 0; i < tiles.length; i++) {
    const t = tiles[i]
    if (!t) continue
    const tx = x + i * (tileW + gap)
    // Drop-shadow letterpress block — solid 8px offset.
    ctx.fillStyle = '#17131d'
    roundRect(ctx, tx + 8, y + 10, tileW, tileH, 18)
    ctx.fill()
    ctx.fillStyle = t.bg
    roundRect(ctx, tx, y, tileW, tileH, 18)
    ctx.fill()
    ctx.strokeStyle = '#17131d'
    ctx.lineWidth = 4
    roundRect(ctx, tx, y, tileW, tileH, 18)
    ctx.stroke()

    // Value — Bagel Fat One large, autoshrinks long strings (e.g. "4h 12m").
    ctx.fillStyle = t.fg
    ctx.textAlign = 'left'
    ctx.textBaseline = 'alphabetic'
    const valueFontSize = t.value.length > 3 ? 64 : 96
    ctx.font = `400 ${valueFontSize}px 'Bagel Fat One','Atkinson Hyperlegible',sans-serif`
    ctx.fillText(t.value, tx + 24, y + tileH - 80)

    // Label — Special Elite mono, lower-cased.
    ctx.font = "500 28px 'Special Elite','Courier New',monospace"
    ctx.fillText(t.label.toUpperCase(), tx + 24, y + tileH - 36)
  }
}

type PolaroidSlot = { x: number; y: number; w: number; h: number; rot: number }

function polaroidLayout(
  area: { x: number; y: number; w: number; h: number },
  n: number,
): PolaroidSlot[] {
  // Up to 6 polaroids arranged in a tossed-on-page composition. We honour the
  // canon screenshot — three loose anchors at the top, three lower — and
  // scale offsets to the available area so the layout never overflows.
  const W = 290
  const H = 340
  const tilts = [-3, 4, -2, 5, -4, 2]
  const anchors: { dx: number; dy: number }[] = [
    { dx: 0.06, dy: 0.0 },
    { dx: 0.5, dy: 0.08 },
    { dx: 0.7, dy: 0.0 },
    { dx: 0.0, dy: 0.45 },
    { dx: 0.36, dy: 0.5 },
    { dx: 0.66, dy: 0.5 },
  ]
  return anchors.slice(0, n).map((a, i) => ({
    x: area.x + a.dx * (area.w - W),
    y: area.y + a.dy * (area.h - H),
    w: W,
    h: H,
    rot: tilts[i] ?? 0,
  }))
}

function drawPolaroidGrid(
  ctx: AnyCtx,
  area: { x: number; y: number; w: number; h: number },
  cards: PolaroidCard[],
  photos: (HTMLImageElement | null)[],
  rand: () => number,
): void {
  const n = Math.max(cards.length, photos.filter((p) => !!p).length)
  if (n === 0) {
    // Empty fallback — single placeholder polaroid that reads "no photos yet".
    drawPolaroid(ctx, {
      slot: { x: area.x + area.w / 2 - 145, y: area.y + 100, w: 290, h: 340, rot: -2 },
      caption: 'add a selfie next time',
      image: null,
      placeholderColor: RECAP_PALETTE.LIME,
    })
    return
  }
  const count = Math.min(n, 6)
  const slots = polaroidLayout(area, count)
  for (let i = 0; i < count; i++) {
    const slot = slots[i]
    if (!slot) continue
    const card = cards[i]
    const photo = photos[i] ?? null
    drawPolaroid(ctx, {
      slot,
      caption: card?.caption ?? '—',
      image: photo,
      // Cycle through brand colours for placeholders so missing photos still
      // look intentional.
      placeholderColor: PLACEHOLDER_COLORS[i % PLACEHOLDER_COLORS.length] ?? RECAP_PALETTE.LIME,
      // Mild per-photo crop offset using the seeded RNG so every render of
      // the same night picks the same framing.
      cropOffset: { x: 0.4 + rand() * 0.2, y: 0.35 + rand() * 0.2 },
    })
  }
}

const PLACEHOLDER_COLORS: readonly string[] = [
  RECAP_PALETTE.LIME,
  RECAP_PALETTE.TEAL,
  RECAP_PALETTE.PINK,
  RECAP_PALETTE.ORANGE,
  RECAP_PALETTE.ACID,
  RECAP_PALETTE.LIME,
]

function drawPolaroid(
  ctx: AnyCtx,
  opts: {
    slot: PolaroidSlot
    caption: string
    image: HTMLImageElement | null
    placeholderColor: string
    cropOffset?: { x: number; y: number }
  },
): void {
  const { slot, caption, image, placeholderColor, cropOffset } = opts
  const { x, y, w, h, rot } = slot

  ctx.save()
  ctx.translate(x + w / 2, y + h / 2)
  ctx.rotate((rot * Math.PI) / 180)
  ctx.translate(-w / 2, -h / 2)

  // Frame shadow then white border, then photo well, then Caveat caption.
  ctx.fillStyle = 'rgba(23,19,29,0.22)'
  roundRect(ctx, 6, 8, w, h, 4)
  ctx.fill()

  ctx.fillStyle = RECAP_PALETTE.PAPER
  ctx.strokeStyle = '#17131d'
  ctx.lineWidth = 3
  roundRect(ctx, 0, 0, w, h, 4)
  ctx.fill()
  ctx.stroke()

  // Photo well — slight inset.
  const pad = 16
  const wellH = h - pad * 2 - 60 // 60 = caption strip
  ctx.fillStyle = placeholderColor
  ctx.fillRect(pad, pad, w - pad * 2, wellH)

  if (image) {
    // Cover-fit the image into the well, with seeded crop offset.
    const wellW = w - pad * 2
    const ar = image.width / image.height
    const wellAr = wellW / wellH
    let sx = 0
    let sy = 0
    let sw = image.width
    let sh = image.height
    if (ar > wellAr) {
      sw = image.height * wellAr
      sx = (image.width - sw) * (cropOffset?.x ?? 0.5)
    } else {
      sh = image.width / wellAr
      sy = (image.height - sh) * (cropOffset?.y ?? 0.5)
    }
    ctx.save()
    ctx.beginPath()
    ctx.rect(pad, pad, wellW, wellH)
    ctx.clip()
    ctx.drawImage(image, sx, sy, sw, sh, pad, pad, wellW, wellH)
    ctx.restore()
  }

  // Caption — Caveat handwriting.
  ctx.fillStyle = '#17131d'
  ctx.font = "500 36px 'Caveat','Atkinson Hyperlegible',cursive"
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(truncate(caption, 18), w / 2, h - 32)

  ctx.restore()
}

function drawLeaderboardChip(
  ctx: AnyCtx,
  x: number,
  y: number,
  w: number,
  rows: ScrapbookLeaderboardEntry[],
): void {
  // White ribbon at the bottom with up to 3 rows of "#N name · venue · K drinks".
  const h = 200
  ctx.fillStyle = '#17131d'
  roundRect(ctx, x + 6, y + 8, w, h, 16)
  ctx.fill()
  ctx.fillStyle = RECAP_PALETTE.PAPER
  ctx.strokeStyle = '#17131d'
  ctx.lineWidth = 4
  roundRect(ctx, x, y, w, h, 16)
  ctx.fill()
  ctx.stroke()

  ctx.fillStyle = RECAP_PALETTE.INK
  ctx.font = "500 28px 'Special Elite','Courier New',monospace"
  ctx.textAlign = 'left'
  ctx.textBaseline = 'alphabetic'
  ctx.fillText('WHO ELSE WAS OUT', x + 28, y + 44)

  ctx.font = "400 36px 'Bagel Fat One','Atkinson Hyperlegible',sans-serif"
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i]
    if (!r) continue
    const rowY = y + 92 + i * 36
    const venuePiece = r.venueName ? ` · ${truncate(r.venueName, 16)}` : ''
    const drinksPiece = ` · ${r.drinkCount} drink${r.drinkCount === 1 ? '' : 's'}`
    ctx.fillStyle = r.isSelf ? RECAP_PALETTE.PINK : RECAP_PALETTE.INK
    ctx.fillText(`#${r.rank} ${r.name}${venuePiece}${drinksPiece}`, x + 28, rowY)
  }
}

function drawWrappedText(
  ctx: AnyCtx,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  maxLines: number,
): void {
  const words = text.split(/\s+/)
  const lines: string[] = []
  let current = ''
  for (const word of words) {
    const test = current ? `${current} ${word}` : word
    if (ctx.measureText(test).width <= maxWidth) {
      current = test
    } else {
      if (current) lines.push(current)
      current = word
    }
  }
  if (current) lines.push(current)
  const truncated = lines.slice(0, maxLines)
  for (let i = 0; i < truncated.length; i++) {
    const line = truncated[i] ?? ''
    ctx.fillText(line, x, y + i * lineHeight)
  }
}

/**
 * F3: one polaroid card = one image source + a caption. Built from
 * `metadata.selfies[]` when present so N selfies render as N cards (fixes
 * "3 selfies, 2 polaroids" bug where venue-grouping collapsed duplicates).
 * Falls back to legacy per-venue grouping for older callers.
 */
type PolaroidCard = {
  source: Blob | string
  /** First line under the photo — venue name or "—". */
  caption: string
}

function buildPolaroidCards(blobs: Blob[], metadata: ScrapbookMetadata): PolaroidCard[] {
  const cards: PolaroidCard[] = []
  // Pre-supplied blobs (Wave 3 test path) get a generic caption since they're
  // not associated with a venue. Real callers (recap.tsx) pass [].
  for (const blob of blobs) {
    if (cards.length >= 6) break
    cards.push({ source: blob, caption: 'tonight' })
  }
  // F3 path — explicit per-selfie array. Each selfie → one polaroid.
  if (metadata.selfies && metadata.selfies.length > 0) {
    for (const selfie of metadata.selfies) {
      if (cards.length >= 6) break
      cards.push({ source: selfie.url, caption: selfie.venueName || '—' })
    }
    return cards
  }
  // Legacy fallback: one photo per venue.
  for (const v of metadata.venues) {
    if (cards.length >= 6) break
    if (v.photoUrl) cards.push({ source: v.photoUrl, caption: v.name })
  }
  return cards
}

function loadImage(src: Blob | string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = (e) => reject(new Error(`scrapbook image load failed: ${String(e)}`))
    img.src = typeof src === 'string' ? src : URL.createObjectURL(src)
  })
}

function formatDate(d: Date): string {
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
}

function formatElapsed(start: Date, end: Date | null): string {
  const finish = end ?? new Date()
  const ms = Math.max(finish.getTime() - start.getTime(), 0)
  const totalMin = Math.round(ms / 60000)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h === 0) return `${m}m`
  return `${h}h ${m.toString().padStart(2, '0')}m`
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s
  return `${s.slice(0, max - 1).trimEnd()}…`
}

function roundRect(ctx: AnyCtx, x: number, y: number, w: number, h: number, r: number): void {
  const radius = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.lineTo(x + w - radius, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius)
  ctx.lineTo(x + w, y + h - radius)
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h)
  ctx.lineTo(x + radius, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius)
  ctx.lineTo(x, y + radius)
  ctx.quadraticCurveTo(x, y, x + radius, y)
  ctx.closePath()
}
